const TrackingQueryParamPrefixes = ['utm_'] as const;

const TrackingQueryParams = new Set([
  'fbclid',
  'gclid',
  'mc_cid',
  'mc_eid',
  'igshid'
]);

function isTrackingQueryParam(rawName: string): boolean {
  const name = rawName.trim().toLowerCase();
  if (TrackingQueryParams.has(name)) {
    return true;
  }
  return TrackingQueryParamPrefixes.some((prefix) => name.startsWith(prefix));
}

export function extractSingleHttpUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return null;
  }

  const candidate = hasUrlScheme(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    if (!hasUrlScheme(trimmed) && !isCleanHttpUrlHost(url.hostname)) {
      return null;
    }
    return candidate;
  } catch {
    return null;
  }
}

function hasUrlScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value);
}

function isCleanHttpUrlHost(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();
  if (normalized === 'localhost') {
    return true;
  }
  const labels = normalized.split('.');
  if (labels.length < 2) {
    return false;
  }
  const tld = labels[labels.length - 1];
  return /^[a-z]{2,}$/.test(tld);
}

export function normalizeUrlIdentity(rawUrl: string): string {
  let normalized: URL;
  try {
    normalized = new URL(rawUrl);
  } catch {
    return rawUrl.trim();
  }

  normalized.hash = '';
  normalized.username = '';
  normalized.password = '';
  normalized.hostname = normalized.hostname.toLowerCase();
  normalized.protocol = normalized.protocol.toLowerCase();

  const keptParams: Array<[string, string]> = [];
  for (const [name, value] of normalized.searchParams.entries()) {
    if (isTrackingQueryParam(name)) {
      continue;
    }
    keptParams.push([name, value]);
  }

  keptParams.sort(([leftName, leftValue], [rightName, rightValue]) => {
    if (leftName < rightName) return -1;
    if (leftName > rightName) return 1;
    if (leftValue < rightValue) return -1;
    if (leftValue > rightValue) return 1;
    return 0;
  });

  normalized.search = '';
  for (const [name, value] of keptParams) {
    normalized.searchParams.append(name, value);
  }

  return normalized.toString();
}
